import { snapshot as authSnapshot } from "@internal/auth";
import { getDb, type PlannerDoc } from "@/lib/db";
import {
  normalizeSymptoms,
  PAIN_ENTRY_TYPE,
  type PainEntryData,
  painEntryId,
} from "@/lib/pain";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

export interface PainEntryPatch {
  level?: number | null; // null clears the Smertenivå
  symptoms?: PainEntryData["symptoms"];
  note?: string; // blank clears the note
}

/**
 * Upsert the Pain entry for `date`. Rows are never deleted — an entry whose
 * fields all become empty stays as an empty row, as body measurements do.
 */
export async function updatePainEntry(
  date: string,
  patch: PainEntryPatch,
): Promise<void> {
  const userId = currentUserId();
  const db = await getDb();
  const id = painEntryId(userId, date);
  await enqueue(id, async () => {
    const existing = await db.daily_planner.findOne(id).exec();
    const current: PainEntryData = existing
      ? ((existing.toJSON() as PlannerDoc).data as unknown as PainEntryData)
      : { date, symptoms: [] };
    const next: PainEntryData = {
      date,
      symptoms: normalizeSymptoms(patch.symptoms ?? current.symptoms ?? []),
    };
    const level = patch.level === undefined ? current.level : patch.level;
    if (typeof level === "number") next.level = level;
    const note = (patch.note ?? current.note ?? "").trim();
    if (note.length > 0) next.note = note;
    if (existing) {
      await existing.patch({
        data: next as unknown as Record<string, unknown>,
      });
    } else {
      await db.daily_planner.insert({
        id,
        type: PAIN_ENTRY_TYPE,
        user_id: userId,
        data: next as unknown as Record<string, unknown>,
      });
    }
  });
}
